import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { Text } from "../../../../../../components/common/typography";
import type { CountryCode } from "../../../../domain/register-wizard";
import { GCC_COUNTRIES } from "../../../../data/gcc-countries";

interface CountryPickerProps {
  value: CountryCode | "";
  onChange: (code: CountryCode) => void;
  label?: string;
  placeholder?: string;
}

export function CountryPicker({
  value,
  onChange,
  label = "Country",
  placeholder = "Select country",
}: CountryPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = GCC_COUNTRIES.find((c) => c.code === value);

  return (
    <div ref={rootRef} className="relative flex w-full flex-col gap-1.5">
      <label className="px-1.5">
        <Text variant="bodySmBold" inline tone="body">
          {label}
        </Text>
      </label>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={clsx(
          "flex h-[54px] w-full items-center gap-1.5 rounded-2xl border bg-bgWhite px-3 py-4",
          "transition-colors focus:outline-none",
          open ? "border-textBody" : "border-borderDefault",
        )}
      >
        <span className="flex-1 text-left">
          <Text
            variant={selected ? "bodySmBold" : "bodySm"}
            tone={selected ? "dark" : "body"}
            inline
          >
            {selected ? selected.label : placeholder}
          </Text>
        </span>
        <IconChevronDown
          size={20}

          className={clsx(
            "shrink-0 text-textBody transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <ul
          role="listbox"
          className="mt-2 max-h-[260px] overflow-y-auto rounded-2xl bg-borderLight shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]"
        >
          {GCC_COUNTRIES.map((country) => {
            const isSelected = country.code === value;
            return (
              <li key={country.code} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(country.code);
                    setOpen(false);
                  }}
                  className="flex h-[54px] w-full items-center px-3 py-4 text-left hover:bg-bgMuted"
                >
                  <Text
                    variant={isSelected ? "bodySmBold" : "bodySm"}
                    tone={isSelected ? "dark" : "body"}
                    inline
                  >
                    {country.label}
                  </Text>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
